// =============================================================================
// MeuDriver — Instructor Lessons List
// =============================================================================

import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MapPin, Clock, Star, ChevronDown, User, MessageSquare, Send, X, Users, ChevronRight, ThumbsUp, FileText, Save, Check, Target, CalendarClock, Megaphone } from 'lucide-react'
import { CalendarExportButton } from '@/components/shared/CalendarExportButton'
import type { BookingForExport } from '@/hooks/useCalendarExport'
import { SkillRatingForm } from '@/components/instructor/SkillRatingForm'
import { useAuth } from '@/hooks/useAuth'
import { useInstructorBookings } from '@/hooks/useBookings'
import { useRealtimeMessages } from '@/hooks/useRealtime'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import {
  formatCurrency,
  formatDateTime,
  getBookingStatusLabel,
  getBookingStatusColor,
  getInitials,
} from '@/lib/utils'
import { ChatTemplateSelector } from '@/components/instructor/ChatTemplateSelector'
import type { BookingStatus, NotificationInsert, ProfileRow, BookingRow } from '@/types/database'

// ---------------------------------------------------------------------------
// Broadcast aos alunos de hoje
// ---------------------------------------------------------------------------

interface TodayBookingLite {
  student_id: string
  id: string
}

function BroadcastTodayCard({
  bookings,
  instructorName,
}: {
  bookings: TodayBookingLite[]
  instructorName: string | null
}) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  if (bookings.length === 0) return null

  async function handleSend() {
    const trimmed = message.trim()
    if (trimmed.length < 5) {
      toast.error('Escreva uma mensagem de pelo menos 5 caracteres.')
      return
    }
    setSending(true)
    try {
      // Insere uma mensagem em cada booking + uma notificação para cada aluno.
      // Loop sequencial para coletar erros parciais (Supabase JS não tem bulk
      // inter-tabela; faz-se 2 inserts por aluno).
      let okCount = 0
      const senderProfileId = (await supabase.auth.getUser()).data.user?.id ?? null
      for (const b of bookings) {
        const msgInsert = senderProfileId
          ? await supabase.from('messages').insert({
              booking_id: b.id,
              sender_id: senderProfileId,
              content: trimmed,
            })
          : { error: null }
        if (msgInsert.error) continue

        // O trigger trg_notify_message_insert já cria a notification automaticamente,
        // mas pra garantir um link específico de broadcast adicionamos uma extra.
        const notifInsert = await supabase
          .from('notifications')
          .insert({
            user_id: b.student_id,
            type: 'broadcast_today',
            title: `Aviso de ${instructorName ?? 'seu instrutor'}`,
            body: trimmed.slice(0, 140),
            link: `/aluno/aulas/${b.id}`,
          })
        if (!notifInsert.error) okCount++
      }
      toast.success(`Aviso enviado para ${okCount} aluno(s).`)
      setOpen(false)
      setMessage('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar broadcast.')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/50 p-3">
        <Megaphone className="h-5 w-5 text-amber-700 shrink-0" />
        <div className="flex-1 text-sm">
          <p className="font-medium text-amber-900">
            {bookings.length} aula{bookings.length > 1 ? 's' : ''} hoje
          </p>
          <p className="text-xs text-amber-800/80">
            Envie um aviso rápido para todos os alunos de hoje (ex: atraso, mudança de local).
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setOpen(true)}
          className="bg-amber-600 hover:bg-amber-700 shrink-0"
        >
          <Megaphone className="h-3.5 w-3.5 mr-1.5" />
          Avisar alunos de hoje
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-amber-600" />
              Aviso aos alunos de hoje
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              A mesma mensagem será enviada para os {bookings.length} aluno
              {bookings.length > 1 ? 's' : ''} com aulas hoje.
            </p>
            <Textarea
              rows={5}
              maxLength={500}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ex: Estou no trânsito, vou chegar 10 min atrasado. Aguardem no ponto combinado."
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/500
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending}>
              Cancelar
            </Button>
            <Button onClick={handleSend} disabled={sending} className="bg-amber-600 hover:bg-amber-700">
              {sending ? 'Enviando...' : 'Enviar aviso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ---------------------------------------------------------------------------
// Reschedule proposal (localStorage-backed)
// ---------------------------------------------------------------------------

interface RescheduleProposal {
  proposedAt: string
  proposedBy: 'instructor' | 'student'
  status: 'pending' | 'accepted' | 'rejected'
}

function getRescheduleKey(bookingId: string) {
  return `md-reschedule-${bookingId}`
}

function loadProposal(bookingId: string): RescheduleProposal | null {
  try {
    const raw = localStorage.getItem(getRescheduleKey(bookingId))
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function saveProposal(bookingId: string, p: RescheduleProposal) {
  localStorage.setItem(getRescheduleKey(bookingId), JSON.stringify(p))
}

// ---------------------------------------------------------------------------
// Reschedule modal
// ---------------------------------------------------------------------------

interface RescheduleModalProps {
  booking: BookingWithStudent
  onClose: () => void
  onDone: () => void
}

function RescheduleModal({ booking, onClose, onDone }: RescheduleModalProps) {
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [saving, setSaving] = useState(false)
  const today = new Date().toISOString().slice(0, 10)

  async function handleConfirm() {
    if (!date || !time) return
    setSaving(true)
    try {
      saveProposal(booking.id, {
        proposedAt: `${date}T${time}:00`,
        proposedBy: 'instructor',
        status: 'pending',
      })
      await createNotification({
        user_id: booking.student_id,
        type: 'booking_rescheduled',
        title: 'Proposta de reagendamento',
        body: 'Seu instrutor propôs um novo horário para sua aula. Acesse para confirmar.',
        link: `/aluno/aulas/${booking.id}`,
      }).catch(() => null)
      toast.success('Proposta enviada ao aluno!')
      onDone()
    } catch {
      toast.error('Erro ao enviar proposta.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-brand-600" />
            Propor reagendamento
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          Aula atual: <strong>{formatDateTime(booking.scheduled_at)}</strong>
        </p>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Nova data</Label>
            <Input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Novo horário</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-9" />
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
          <Button
            onClick={handleConfirm}
            disabled={!date || !time || saving}
            className="flex-1"
          >
            {saving ? 'Enviando...' : 'Enviar proposta'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Notification helper
// ---------------------------------------------------------------------------

async function createNotification(n: NotificationInsert) {
  await supabase.from('notifications').insert(n).throwOnError()
}

// ---------------------------------------------------------------------------
// Chat panel (rendered inside Dialog)
// ---------------------------------------------------------------------------

function ChatPanel({
  bookingId,
  studentName,
  instructorId,
}: {
  bookingId: string
  studentName: string
  instructorId: string
}) {
  const { messages, sendMessage, isLoading } = useRealtimeMessages(bookingId)
  const { user } = useAuth()
  const [input, setInput] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function handleSend(e: React.FormEvent) {
    e.preventDefault()
    if (!input.trim()) return
    try {
      await sendMessage(input)
      setInput('')
    } catch {
      toast.error('Erro ao enviar mensagem')
    }
  }

  return (
    <div className="flex flex-col h-[420px]">
      <p className="text-xs text-muted-foreground mb-3">Conversa com {studentName}</p>

      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-8 w-1/2 ml-auto" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            Nenhuma mensagem ainda. Inicie a conversa!
          </p>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_id === user?.id
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${
                    isMe
                      ? 'bg-teal-600 text-white rounded-br-sm'
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="mt-3 border-t pt-3 space-y-2">
        {/* Seletor de templates — preenche o input sem enviar */}
        <ChatTemplateSelector
          instructorId={instructorId}
          onSelect={(body) => setInput(body)}
        />
        <form onSubmit={handleSend} className="flex gap-2">
          <input
            type="text"
            className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Escreva uma mensagem..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <Button type="submit" size="icon" className="h-9 w-9 bg-teal-600 hover:bg-teal-700">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BookingWithStudent = BookingRow & {
  student?: Pick<ProfileRow, 'full_name' | 'avatar_url'>
}

type TabKey = 'upcoming' | 'completed' | 'cancelled' | 'all' | 'students'

const PAGE_SIZE = 10

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusMatchesTab(status: BookingStatus, tab: TabKey): boolean {
  if (tab === 'all') return true
  if (tab === 'upcoming') return status === 'confirmed' || status === 'in_progress'
  if (tab === 'completed') return status === 'completed'
  if (tab === 'cancelled')
    return (
      status === 'cancelled_by_student' ||
      status === 'cancelled_by_instructor' ||
      status === 'no_show'
    )
  return false
}

// Normalise cancelled variants for display
function displayStatus(status: BookingStatus): string {
  if (status === 'cancelled_by_student') return 'cancelled'
  if (status === 'cancelled_by_instructor') return 'cancelled'
  return status
}

// ---------------------------------------------------------------------------
// Rate Student Modal
// ---------------------------------------------------------------------------

interface RateStudentModalProps {
  bookingId: string
  instructorId: string
  studentId: string
  studentName: string
  onClose: () => void
  onDone: () => void
}

function StarPicker({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0)
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-gray-700 w-28 shrink-0">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            onMouseEnter={() => setHovered(s)}
            onMouseLeave={() => setHovered(0)}
            className="focus:outline-none"
          >
            <Star
              className={`h-6 w-6 transition-colors ${
                s <= (hovered || value) ? 'fill-orange-400 text-orange-400' : 'fill-gray-100 text-gray-200'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  )
}

function RateStudentModal({ bookingId, instructorId, studentId, studentName, onClose, onDone }: RateStudentModalProps) {
  const [punctuality, setPunctuality] = useState(5)
  const [behavior,    setBehavior   ] = useState(5)
  const [attention,   setAttention  ] = useState(5)
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)

  const { data: existing } = useQuery({
    queryKey: ['student-rating', bookingId],
    queryFn: async () => {
      const { data } = await supabase
        .from('student_ratings')
        .select('id, punctuality, behavior, attention, comment')
        .eq('booking_id', bookingId)
        .maybeSingle()
      return data
    },
  })

  useEffect(() => {
    if (existing) {
      setPunctuality(existing.punctuality)
      setBehavior(existing.behavior)
      setAttention(existing.attention)
      setComment(existing.comment ?? '')
    }
  }, [existing])

  async function handleSave() {
    setSaving(true)
    try {
      if (existing?.id) {
        await supabase
          .from('student_ratings')
          .update({ punctuality, behavior, attention, comment: comment.trim() || null })
          .eq('id', existing.id)
          .throwOnError()
      } else {
        await supabase
          .from('student_ratings')
          .insert({ booking_id: bookingId, instructor_id: instructorId, student_id: studentId, punctuality, behavior, attention, comment: comment.trim() || null })
          .throwOnError()
      }
      toast.success('Avaliação do aluno salva!')
      onDone()
    } catch {
      toast.error('Erro ao salvar avaliação.')
    } finally {
      setSaving(false)
    }
  }

  const avg = ((punctuality + behavior + attention) / 3).toFixed(1)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <ThumbsUp className="h-5 w-5 text-teal-600" />
            Avaliar aluno
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground">
          Como foi o desempenho de <strong>{studentName}</strong>?
        </p>

        <div className="space-y-3">
          <StarPicker label="Pontualidade"   value={punctuality} onChange={setPunctuality} />
          <StarPicker label="Comportamento"  value={behavior}    onChange={setBehavior}    />
          <StarPicker label="Atenção"        value={attention}   onChange={setAttention}   />
        </div>

        <div className="bg-gray-50 rounded-xl px-4 py-2 text-center">
          <span className="text-2xl font-bold text-teal-600">{avg}</span>
          <span className="text-sm text-muted-foreground ml-1">/ 5.0 média</span>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Observação (opcional)</label>
          <textarea
            rows={2}
            maxLength={300}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Ex.: Aluno muito dedicado, mas precisa praticar mais estacionamento."
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-teal-400"
          />
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-teal-600 hover:bg-teal-700"
          >
            {saving ? 'Salvando...' : existing ? 'Atualizar' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lesson Note (post-lesson instructor note, localStorage)
// ---------------------------------------------------------------------------

function LessonNoteSection({ bookingId }: { bookingId: string }) {
  const [expanded, setExpanded] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    supabase
      .from('bookings')
      .select('instructor_note')
      .eq('id', bookingId)
      .single()
      .then(({ data }) => {
        const n = (data as any)?.instructor_note ?? ''
        setNote(n)
        setDraft(n)
      })
  }, [bookingId])

  async function handleSave() {
    setSaving(true)
    const { error } = await supabase
      .from('bookings')
      .update({ instructor_note: draft.trim() || null } as any)
      .eq('id', bookingId)
    setSaving(false)
    if (error) {
      toast.error('Erro ao salvar nota.')
    } else {
      setNote(draft.trim() || null)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    }
  }

  if (note === null) return null

  return (
    <div className="mt-2 border-t pt-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-teal-700 transition-colors"
      >
        <FileText className="h-3.5 w-3.5" />
        {note ? 'Ver nota da aula' : 'Adicionar nota'}
        <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          <textarea
            rows={2}
            maxLength={400}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Observações sobre esta aula, evolução do aluno, pontos a trabalhar..."
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-teal-400"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700 transition-colors disabled:opacity-60"
            >
              {saved ? <Check className="h-3 w-3" /> : <Save className="h-3 w-3" />}
              {saved ? 'Salvo!' : 'Salvar nota'}
            </button>
            <span className="text-[11px] text-muted-foreground">{draft.length}/400</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Booking Card
// ---------------------------------------------------------------------------

function BookingCard({
  booking,
  onRefresh,
  onOpenChat,
}: {
  booking: BookingWithStudent
  onRefresh: () => void
  onOpenChat: (bookingId: string, studentName: string) => void
}) {
  const [actionLoading, setActionLoading] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewRating, setReviewRating] = useState<number | null>(null)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewId, setReviewId] = useState<string | null>(null)
  const [reviewReply, setReviewReply] = useState('')
  const [replyDraft, setReplyDraft] = useState('')
  const [replyEditing, setReplyEditing] = useState(false)
  const [replySaving, setReplySaving] = useState(false)
  const [showRateStudentModal, setShowRateStudentModal] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [showReschedule, setShowReschedule] = useState(false)
  const [rescheduleProposal, setRescheduleProposalState] = useState<RescheduleProposal | null>(
    () => loadProposal(booking.id),
  )
  const { user } = useAuth()

  const studentName = booking.student?.full_name ?? 'Aluno'

  async function handleStart() {
    setActionLoading(true)
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'in_progress' })
      .eq('id', booking.id)
    setActionLoading(false)
    if (error) {
      toast.error('Erro ao iniciar aula.')
    } else {
      toast.success('Aula iniciada!')
      createNotification({
        user_id: booking.student_id,
        type: 'booking_started',
        title: 'Sua aula foi iniciada!',
        body: `O instrutor iniciou sua aula agendada.`,
        link: `/aluno/aulas/${booking.id}`,
      }).catch(() => null)
      onRefresh()
    }
  }

  async function handleComplete() {
    setActionLoading(true)
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'completed' })
      .eq('id', booking.id)
    setActionLoading(false)
    if (error) {
      toast.error('Erro ao concluir aula.')
    } else {
      toast.success('Aula concluída!')
      createNotification({
        user_id: booking.student_id,
        type: 'booking_completed',
        title: 'Aula concluída!',
        body: 'Sua aula foi concluída. Que tal avaliar o instrutor?',
        link: `/aluno/aulas/${booking.id}`,
      }).catch(() => null)
      onRefresh()
    }
  }

  async function handleCancel() {
    if (!cancelReason.trim()) {
      toast.error('Informe o motivo do cancelamento.')
      return
    }
    setActionLoading(true)
    const { data: result, error } = await supabase.rpc('process_cancellation', {
      p_booking_id:   booking.id,
      p_reason:       cancelReason.trim(),
      p_cancelled_by: 'instructor',
    })
    setActionLoading(false)
    if (error) {
      toast.error(`Erro ao cancelar aula: ${error.message}`)
    } else {
      const res = result as { refund_amount?: number } | null
      const refundMsg = res?.refund_amount && (res.refund_amount as number) > 0
        ? ` O aluno receberá reembolso de R$ ${(res.refund_amount as number).toFixed(2).replace('.', ',')}.`
        : ''
      toast.success(`Aula cancelada.${refundMsg}`)
      createNotification({
        user_id: booking.student_id,
        type: 'booking_cancelled',
        title: 'Aula cancelada pelo instrutor',
        body: `O instrutor cancelou sua aula. Motivo: ${cancelReason.trim()}`,
        link: `/aluno/aulas/${booking.id}`,
      }).catch(() => null)
      setShowCancelDialog(false)
      setCancelReason('')
      onRefresh()
    }
  }

  async function handleMarkNoShow() {
    if (!confirm(
      'Confirmar que o aluno não compareceu? Isso muda o status para "no-show" e ' +
      'inicia a cobrança parcial conforme política (até 50% do valor).',
    )) return
    setActionLoading(true)
    try {
      // Step 1: muda status no DB. Por design do trigger, a edge function
      // `process-no-show` espera que `status='no_show'` para processar refund.
      const { error: statusErr } = await supabase
        .from('bookings')
        .update({ status: 'no_show' })
        .eq('id', booking.id)
      if (statusErr) throw statusErr

      // Step 2: chama edge function para acionar partial refund no MP
      const { data: sessionData } = await supabase.auth.getSession()
      const jwt = sessionData.session?.access_token
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-no-show`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({ booking_id: booking.id }),
      })
      const result = await res.json()
      if (!res.ok) {
        // Status já foi mudado — informa o instrutor que o refund ainda precisa ser processado
        toast.error(
          `Aula marcada como no-show, mas o processamento do reembolso falhou: ${result.error ?? 'erro'}. ` +
          'O admin precisará processar manualmente.',
          { duration: 8000 },
        )
      } else {
        toast.success(
          `Aluno marcado como ausente. Cobrança: ${formatCurrency(Number(result.charge_amount ?? 0))}, ` +
          `reembolso: ${formatCurrency(Number(result.refund_amount ?? 0))}.`,
          { duration: 8000 },
        )
        createNotification({
          user_id: booking.student_id,
          type: 'booking_no_show',
          title: 'Você não compareceu à aula',
          body: 'Aula marcada como ausência. Foi aplicada cobrança parcial conforme nossa política.',
          link: `/aluno/aulas/${booking.id}`,
        }).catch(() => null)
      }
      onRefresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao processar no-show.')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleViewReview() {
    const { data } = await supabase
      .from('reviews')
      .select('id, rating, comment, instructor_reply')
      .eq('booking_id', booking.id)
      .single()
    if (data) {
      setReviewId(data.id)
      setReviewRating(data.rating)
      setReviewComment(data.comment ?? '')
      setReviewReply(data.instructor_reply ?? '')
      setReplyDraft(data.instructor_reply ?? '')
      setReplyEditing(false)
      setShowReviewModal(true)
    } else {
      toast.info('Nenhuma avaliação para esta aula ainda.')
    }
  }

  const colorClass = getBookingStatusColor(displayStatus(booking.status))

  return (
    <>
      <Card className="hover:shadow-sm transition-shadow">
        <CardContent className="p-4">
          <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Avatar */}
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 font-semibold text-sm">
              {getInitials(studentName)}
            </div>

            {/* Main info */}
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-gray-900">{studentName}</p>
                <Badge className={`${colorClass} border-0 text-xs`}>
                  {getBookingStatusLabel(displayStatus(booking.status))}
                </Badge>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {formatDateTime(booking.scheduled_at)}
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  {booking.duration_minutes} min
                </span>
                {booking.pickup_address && (
                  <span className="flex items-center gap-1 max-w-xs truncate">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    {booking.pickup_address}
                  </span>
                )}
              </div>

              <p className="text-sm font-medium text-gray-700">
                {booking.total_price != null
                  ? formatCurrency(booking.total_price)
                  : '—'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end sm:gap-1.5">
              {booking.status === 'confirmed' && (
                <>
                  <Button
                    size="sm"
                    onClick={handleStart}
                    disabled={actionLoading}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Iniciar aula
                  </Button>
                  {rescheduleProposal?.status === 'pending' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700">
                      <CalendarClock className="h-3 w-3" />
                      Proposta enviada
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowReschedule(true)}
                      className="gap-1.5 text-brand-700 border-brand-200 hover:bg-brand-50"
                    >
                      <CalendarClock className="h-3.5 w-3.5" />
                      Reagendar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCancelDialog(true)}
                    disabled={actionLoading}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`btn-cancel-booking-${booking.id}`}
                  >
                    Cancelar
                  </Button>
                </>
              )}

              {booking.status === 'in_progress' && (
                <Button
                  size="sm"
                  onClick={handleComplete}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Concluir aula
                </Button>
              )}

              {(booking.status === 'confirmed' || booking.status === 'in_progress') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMarkNoShow}
                  disabled={actionLoading}
                  className="text-orange-700 border-orange-200 hover:bg-orange-50"
                >
                  Marcar no-show
                </Button>
              )}

              {booking.status === 'completed' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleViewReview}
                    className="gap-1.5"
                  >
                    <Star className="h-3.5 w-3.5 text-orange-400" />
                    Ver avaliação
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowRateStudentModal(true)}
                    className="gap-1.5 text-teal-700 border-teal-200 hover:bg-teal-50"
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                    Avaliar aluno
                  </Button>
                </>
              )}
        
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 text-muted-foreground hover:text-teal-700"
                onClick={() => onOpenChat(booking.id, studentName)}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Mensagens
              </Button>
            </div>
          </div>

          {/* Post-lesson note */}
          {booking.status === 'completed' && (
            <LessonNoteSection bookingId={booking.id} />
          )}

          {/* Skill rating form */}
          {booking.status === 'completed' && user && (
            <SkillRatingForm
              bookingId={booking.id}
              studentId={booking.student_id}
              studentName={studentName}
            />
          )}
          </div>
        </CardContent>
      </Card>

      {/* Review modal */}
      {showReviewModal && reviewRating != null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowReviewModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-lg">Avaliação do aluno</h3>
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }, (_, i) => (
                <Star
                  key={i}
                  className={`h-5 w-5 ${i < reviewRating ? 'text-orange-400 fill-orange-400' : 'text-gray-200 fill-gray-200'}`}
                />
              ))}
              <span className="ml-2 font-medium">{reviewRating}/5</span>
            </div>
            {reviewComment && (
              <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-lg p-3">{reviewComment}</p>
            )}

            {/* Existing reply */}
            {reviewReply && !replyEditing && (
              <div className="border-l-2 border-teal-200 pl-3 space-y-1">
                <p className="text-xs font-semibold text-teal-700">Sua resposta pública</p>
                <p className="text-sm text-gray-600 leading-relaxed">{reviewReply}</p>
                <button
                  onClick={() => { setReplyDraft(reviewReply); setReplyEditing(true) }}
                  className="text-xs text-muted-foreground hover:text-teal-600 transition-colors"
                >
                  Editar resposta
                </button>
              </div>
            )}

            {/* Reply prompt */}
            {!reviewReply && !replyEditing && (
              <button
                onClick={() => { setReplyDraft(''); setReplyEditing(true) }}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-teal-600 transition-colors"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Responder avaliação
              </button>
            )}

            {/* Reply editor */}
            {replyEditing && (
              <div className="space-y-2">
                <textarea
                  rows={3}
                  maxLength={400}
                  value={replyDraft}
                  onChange={(e) => setReplyDraft(e.target.value)}
                  placeholder="Escreva sua resposta pública..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-teal-400"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={replySaving || !replyDraft.trim()}
                    onClick={async () => {
                      if (!reviewId) return
                      setReplySaving(true)
                      const { error } = await supabase
                        .from('reviews')
                        .update({ instructor_reply: replyDraft.trim(), instructor_reply_at: new Date().toISOString() } as any)
                        .eq('id', reviewId)
                      setReplySaving(false)
                      if (error) {
                        toast.error('Erro ao salvar resposta.')
                      } else {
                        setReviewReply(replyDraft.trim())
                        setReplyEditing(false)
                        toast.success('Resposta salva!')
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-60 transition-colors"
                  >
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={() => setReplyEditing(false)}
                    className="rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            <Button className="w-full" onClick={() => setShowReviewModal(false)}>
              Fechar
            </Button>
          </div>
        </div>
      )}

      {showRateStudentModal && (
        <RateStudentModal
          bookingId={booking.id}
          instructorId={user?.id ?? ''}
          studentId={booking.student_id}
          studentName={studentName}
          onClose={() => setShowRateStudentModal(false)}
          onDone={() => { setShowRateStudentModal(false); onRefresh() }}
        />
      )}

      {showReschedule && (
        <RescheduleModal
          booking={booking}
          onClose={() => setShowReschedule(false)}
          onDone={() => {
            setRescheduleProposalState(loadProposal(booking.id))
            setShowReschedule(false)
          }}
        />
      )}

      {/* Dialog: cancelar aula (instrutor) */}
      <Dialog open={showCancelDialog} onOpenChange={(open) => { if (!open) { setShowCancelDialog(false); setCancelReason('') } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <X className="h-5 w-5" />
              Cancelar aula
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              O aluno receberá reembolso integral quando o instrutor cancela. Informe o motivo.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="cancel-reason">Motivo do cancelamento</Label>
              <Textarea
                id="cancel-reason"
                placeholder="Ex: Problema de saúde, emergência familiar..."
                rows={3}
                maxLength={300}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                data-testid="input-cancel-reason"
              />
              <p className="text-xs text-muted-foreground text-right">{cancelReason.length}/300</p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowCancelDialog(false); setCancelReason('') }}
                disabled={actionLoading}
              >
                Voltar
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={actionLoading || !cancelReason.trim()}
                data-testid="btn-confirm-instructor-cancel"
              >
                {actionLoading ? 'Cancelando...' : 'Confirmar cancelamento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function BookingCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex gap-4">
          <Skeleton className="h-11 w-11 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-64" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Evolution plan (per-student checklist, localStorage)
// ---------------------------------------------------------------------------

const SKILL_PLAN = [
  'Partida e parada suave',
  'Troca de marchas',
  'Curvas e conversões',
  'Estacionamento paralelo',
  'Estacionamento em ângulo',
  'Baliza / garagem',
  'Direção em rodovias',
  'Direção defensiva',
  'Situações de emergência',
  'Noções de mecânica básica',
]

function EvolutionPlan({ studentId }: { studentId: string }) {
  const key = `md-evolution-${studentId}`
  const [checked, setChecked] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(key) ?? '[]')) }
    catch { return new Set() }
  })
  const [visible, setVisible] = useState(false)

  function toggle(skill: string) {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(skill)) next.delete(skill)
      else next.add(skill)
      localStorage.setItem(key, JSON.stringify([...next]))
      return next
    })
  }

  const pct = Math.round((checked.size / SKILL_PLAN.length) * 100)

  return (
    <div className="border-t bg-gray-50/50 px-4 py-3">
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-teal-700 transition-colors w-full"
      >
        <Target className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 text-left font-medium">Plano de evolução ({pct}%)</span>
        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${visible ? 'rotate-90' : ''}`} />
      </button>

      {visible && (
        <div className="mt-2 space-y-2">
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {SKILL_PLAN.map((skill) => (
              <label key={skill} className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={checked.has(skill)}
                  onChange={() => toggle(skill)}
                  className="h-3.5 w-3.5 rounded accent-teal-600"
                />
                <span className={checked.has(skill) ? 'line-through text-gray-400' : 'text-gray-700'}>
                  {skill}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Student history tab
// ---------------------------------------------------------------------------

interface StudentSummary {
  studentId: string
  name: string
  avatarUrl?: string
  totalLessons: number
  completedLessons: number
  totalHours: number
  lastLesson: string
  avgRating: number | null
}

function StudentHistoryTab({ bookings }: { bookings: BookingWithStudent[] }) {
  const [expanded, setExpanded] = useState<string | null>(null)

  const summaryMap = new Map<string, StudentSummary>()

  for (const b of bookings) {
    const sid = (b as any).student_id ?? 'unknown'
    const name = b.student?.full_name ?? 'Aluno'
    if (!summaryMap.has(sid)) {
      summaryMap.set(sid, {
        studentId: sid,
        name,
        avatarUrl: b.student?.avatar_url ?? undefined,
        totalLessons: 0,
        completedLessons: 0,
        totalHours: 0,
        lastLesson: b.scheduled_at,
        avgRating: null,
      })
    }
    const s = summaryMap.get(sid)!
    s.totalLessons++
    if (b.status === 'completed') {
      s.completedLessons++
      s.totalHours += Math.round((b.duration_minutes ?? 60) / 60)
    }
    if (b.scheduled_at > s.lastLesson) s.lastLesson = b.scheduled_at
  }

  const summaries = Array.from(summaryMap.values()).sort(
    (a, b) => b.lastLesson.localeCompare(a.lastLesson),
  )

  if (summaries.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-14 text-center">
          <Users className="h-10 w-10 text-gray-300 mb-3" />
          <p className="font-medium text-gray-500">Nenhum aluno ainda</p>
          <p className="text-sm text-muted-foreground mt-1">Seus alunos aparecerão aqui após a primeira aula.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      {summaries.map((s) => {
        const studentBookings = bookings.filter(
          (b) => ((b as any).student_id ?? 'unknown') === s.studentId,
        )
        const isOpen = expanded === s.studentId
        const initials = s.name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()

        return (
          <Card key={s.studentId} className="overflow-hidden">
            <button
              className="w-full text-left"
              onClick={() => setExpanded(isOpen ? null : s.studentId)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700 font-bold text-sm">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {s.totalLessons} aula{s.totalLessons !== 1 ? 's' : ''} •{' '}
                      {s.completedLessons} concluída{s.completedLessons !== 1 ? 's' : ''} •{' '}
                      {s.totalHours}h totais
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground hidden sm:block">
                      Última: {formatDateTime(s.lastLesson)}
                    </span>
                    <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </div>
                </div>
              </CardContent>
            </button>

            {isOpen && (
              <div className="divide-y divide-gray-100">
                {studentBookings
                  .sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at))
                  .map((b) => {
                    const color = getBookingStatusColor(b.status as BookingStatus)
                    const label = getBookingStatusLabel(b.status as BookingStatus)
                    return (
                      <div key={b.id} className="flex items-center gap-3 px-4 py-3 text-sm bg-gray-50/50">
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-700">{formatDateTime(b.scheduled_at)}</p>
                          <p className="text-xs text-muted-foreground">{b.duration_minutes} min</p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
                          {label}
                        </span>
                        {b.total_price != null && (
                          <span className="text-xs font-medium text-gray-700 hidden sm:block">
                            {formatCurrency(b.total_price)}
                          </span>
                        )}
                      </div>
                    )
                  })}
                <EvolutionPlan studentId={s.studentId} />
              </div>
            )}
          </Card>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function InstructorLessons() {
  const { user, profile } = useAuth()
  const [activeTab, setActiveTab] = useState<TabKey>('upcoming')
  const [page, setPage] = useState<Record<TabKey, number>>({
    upcoming: 1,
    completed: 1,
    cancelled: 1,
    all: 1,
    students: 1,
  })
  const [chatBookingId, setChatBookingId] = useState<string | null>(null)
  const [chatStudentName, setChatStudentName] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()

  function handleOpenChat(bookingId: string, studentName: string) {
    setChatBookingId(bookingId)
    setChatStudentName(studentName)
  }

  // Busca o instructor.id (UUID da tabela instructors) para o ChatTemplateSelector
  const { data: instructorRow } = useQuery<{ id: string } | null>({
    queryKey: ['instructor-row-id', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user!.id)
        .maybeSingle()
      return data ?? null
    },
    staleTime: 1000 * 60 * 10,
  })

  const {
    data: allBookings = [],
    isLoading,
    refetch,
  } = useInstructorBookings()

  // Auto-open chat when navigated from MessagesPage via ?chat=bookingId.
  // Clear the param immediately so closing the dialog doesn't re-trigger this.
  useEffect(() => {
    const chatId = searchParams.get('chat')
    if (!chatId || allBookings.length === 0) return
    const booking = allBookings.find((b: any) => b.id === chatId) as BookingWithStudent | undefined
    if (booking) {
      handleOpenChat(booking.id, booking.student?.full_name ?? 'Aluno')
      setSearchParams((prev) => { prev.delete('chat'); return prev }, { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allBookings])

  function getFiltered(tab: TabKey) {
    return allBookings.filter((b) => statusMatchesTab(b.status as BookingStatus, tab))
  }

  function getPage(tab: TabKey) {
    const filtered = getFiltered(tab)
    const pageNum = page[tab]
    return filtered.slice(0, pageNum * PAGE_SIZE)
  }

  function hasMore(tab: TabKey) {
    return getFiltered(tab).length > page[tab] * PAGE_SIZE
  }

  function loadMore(tab: TabKey) {
    setPage((prev) => ({ ...prev, [tab]: prev[tab] + 1 }))
  }

  function handleTabChange(tab: string) {
    setActiveTab(tab as TabKey)
  }

  const tabDef: { key: TabKey; label: string }[] = [
    { key: 'upcoming', label: 'Próximas' },
    { key: 'completed', label: 'Concluídas' },
    { key: 'cancelled', label: 'Canceladas' },
    { key: 'all', label: 'Todas' },
    { key: 'students', label: 'Por aluno' },
  ]

  const uniqueStudentCount = new Set(allBookings.map((b: any) => b.student_id)).size

  // Aulas de hoje (para broadcast)
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  const todayEnd = new Date()
  todayEnd.setHours(23, 59, 59, 999)
  const todayBookings = allBookings.filter((b) => {
    const at = new Date(b.scheduled_at)
    return (
      at >= todayStart &&
      at <= todayEnd &&
      (b.status === 'confirmed' || b.status === 'in_progress')
    )
  })

  // Prepara lista de bookings para exportação iCal
  const exportableBookings: BookingForExport[] = allBookings.map((b) => {
    const raw = b as typeof b & { student?: { full_name?: string | null } | null }
    return {
      id:              b.id,
      scheduled_at:   b.scheduled_at,
      duration_minutes: b.duration_minutes ?? 60,
      pickup_address:  b.pickup_address ?? null,
      otherPartyName:  raw.student?.full_name ?? null,
      lessonTypeLabel: 'Aula de direção',
    }
  })

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Minhas aulas</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie e acompanhe todas as suas aulas.
          </p>
        </div>
        <CalendarExportButton
          bookings={exportableBookings}
          filename="meudriver-aulas-instrutor"
          variant="outline"
          size="sm"
        />
      </div>

      <BroadcastTodayCard
        bookings={todayBookings.map((b) => ({ id: b.id, student_id: b.student_id }))}
        instructorName={profile?.full_name ?? null}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-5 mb-4">
          {tabDef.map(({ key, label }) => {
            const count = key === 'students' ? uniqueStudentCount : getFiltered(key).length
            return (
              <TabsTrigger key={key} value={key} className="text-xs sm:text-sm">
                {label}
                {count > 0 && (
                  <span className="ml-1.5 rounded-full bg-current/10 px-1.5 py-0.5 text-[10px] font-medium">
                    {count}
                  </span>
                )}
              </TabsTrigger>
            )
          })}
        </TabsList>

        {tabDef.filter(({ key }) => key !== 'students').map(({ key }) => (
          <TabsContent key={key} value={key} className="space-y-3">
            {isLoading ? (
              <>
                <BookingCardSkeleton />
                <BookingCardSkeleton />
                <BookingCardSkeleton />
              </>
            ) : getFiltered(key).length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-14 text-center">
                  <Clock className="h-10 w-10 text-gray-300 mb-3" />
                  <p className="font-medium text-gray-500">Nenhuma aula encontrada</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {key === 'upcoming'
                      ? 'Você não tem aulas agendadas no momento.'
                      : 'Nenhuma aula nesta categoria ainda.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {getPage(key).map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking as BookingWithStudent}
                    onRefresh={refetch}
                    onOpenChat={handleOpenChat}
                  />
                ))}

                {hasMore(key) && (
                  <div className="flex justify-center pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadMore(key)}
                      className="gap-1.5"
                    >
                      <ChevronDown className="h-4 w-4" />
                      Carregar mais
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        ))}

        <TabsContent value="students" className="space-y-3">
          {isLoading ? (
            <>
              <BookingCardSkeleton />
              <BookingCardSkeleton />
            </>
          ) : (
            <StudentHistoryTab bookings={allBookings as BookingWithStudent[]} />
          )}
        </TabsContent>
      </Tabs>

      {/* Chat dialog */}
      <Dialog open={Boolean(chatBookingId)} onOpenChange={(open) => { if (!open) setChatBookingId(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-teal-600" />
              Chat
            </DialogTitle>
          </DialogHeader>
          {chatBookingId && (
            <ChatPanel
              bookingId={chatBookingId}
              studentName={chatStudentName}
              instructorId={instructorRow?.id ?? ''}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
